import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getOptimizedImageUrl } from '../lib/images';

export default function ClinicalProductionPage() {
  useEffect(() => {
    document.title = 'Online Vejledning til Billeder & Video - Health4media.dk';
    window.scrollTo(0, 0);
  }, []);

  const steps = [
    {
      number: '01',
      title: 'Online sparring & emneudvælgelse',
      desc: 'Vi afdækker patienternes typiske spørgsmål og planlægger konkrete optagelser.'
    },
    {
      number: '02',
      title: 'Vejledning til lys, lyd & vinkler',
      desc: 'Enkle retningslinjer til smartphone-optagelse og fotos direkte i klinikken.'
    },
    {
      number: '03',
      title: 'I optager & sender råfilerne',
      desc: 'I filmer når det passer jer – send blot optagelserne nemt online til os.'
    },
    {
      number: '04',
      title: 'Vi klipper, redigerer & opsætter',
      desc: 'Professionel klipning, undertekster og opsætning af jeres Meta postkalender.'
    }
  ];

  const benefits = [
    {
      title: 'Ingen forstyrrelse af klinikhverdagen',
      desc: 'I slipper for store filmhold og dyrt udstyr i klinikken. I optager i eget tempo mellem patienter eller når det passer bedst.',
      icon: 'fa-door-closed'
    },
    {
      title: 'Konkret mobilvejledning',
      desc: 'Moderne smartphones har fantastiske kameraer. Vi guider jer i præcis de rigtige vinkler, lysforhold og mikrofonløsninger.',
      icon: 'fa-mobile-screen-button'
    },
    {
      title: 'I optager selv – vi klipper alt',
      desc: 'Hun optager ikke selv indholdet for jer, men vejleder jer grundigt. I sender blot råfilerne, og hun redigerer alt det færdige content.',
      icon: 'fa-scissors'
    },
    {
      title: 'Meta postkalender & SoMe-opsætning',
      desc: 'Mulighed for komplet opsætning af jeres sociale medier samt oprettelse og vedligeholdelse af en fast Meta SoMe postkalender.',
      icon: 'fa-calendar-check'
    }
  ];

  return (
    <div className="bg-slate-50 min-h-screen">
      
      {/* HERO SECTION */}
      <section className="bg-slate-900 text-white py-10 sm:py-16 md:py-24 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-7 space-y-4 sm:space-y-6">
              <h1 className="text-2xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-tight">
                Optagelsesvejledning, der opbygger stærk patienttillid
              </h1>
              <p className="text-sm sm:text-base md:text-lg text-slate-300 leading-relaxed font-normal">
                Gør det trygt og ubesværet for patienterne at vælge netop jeres praksis. Vi vejleder jer 100% online til rolige, professionelle smartphone-optagelser, der formidler jeres faglige empati, nedbryder behandlingsangst og tiltrækker nye patienter.
              </p>
              
              <div className="pt-2 sm:pt-4 flex flex-wrap items-center gap-3 sm:gap-4">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 px-6 py-3 sm:px-7 sm:py-3.5 rounded-full bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold text-sm sm:text-base transition shadow-lg shadow-sky-950/40"
                >
                  <span>Book en samtale</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
                <Link
                  to="/cases"
                  className="inline-flex items-center gap-2 px-5 py-3 sm:px-6 sm:py-3.5 rounded-full bg-transparent hover:bg-transparent hover:-translate-y-1 transform text-white font-medium text-xs sm:text-sm transition-all duration-200 border border-white"
                >
                  <span>Se kliniske cases</span>
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative overflow-hidden aspect-4/3 shadow-2xl border border-slate-700 bg-slate-800 max-h-[260px] sm:max-h-none">
                <img
                  src={getOptimizedImageUrl("https://assets.auxodev.dk/assets/48b457c4-b6b2-4d62-b92b-6229aa267ddf/health-mobile-clinic-production.png", { width: 700, height: 525, fit: 'cover' })}
                  alt="Online vejledning til optagelse med smartphone i sundhedsklinik"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* CORE ADVANTAGES */}
      <section className="py-16 sm:py-24 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-5">
              <span className="text-xs font-bold text-sky-700 uppercase tracking-wider block mb-2">
                Enkel & ubesværet model
              </span>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
                Skabt til travle læger, fysioterapeuter og behandlere
              </h2>
              <p className="text-base text-slate-600 mt-3 leading-relaxed">
                Vi forstår klinikkens hverdag. I behøver ikke sætte timer af til et filmhold eller frygte teknisk bøvl. Vi giver jer enkle opskrifter, så I nemt selv kan skyde det nødvendige materiale med telefonen.
              </p>
            </div>

            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              {benefits.map((b, idx) => (
                <div
                  key={idx}
                  className="bg-slate-50 border border-slate-200/80 rounded-2xl p-6 flex flex-col justify-start"
                >
                  <div className="text-2xl text-sky-600 mb-3">
                    <i className={`fa-solid ${b.icon}`} aria-hidden="true" />
                  </div>
                  <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-2">
                    {b.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                    {b.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* HOW THE ONLINE PROCESS WORKS */}
      <section className="py-16 sm:py-24 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-14">
            <span className="text-xs font-bold text-sky-700 uppercase tracking-wider block mb-2">
              Arbejdsgangen
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
              Fra mobiloptagelse til færdigt SoMe content
            </h2>
            <p className="text-base text-slate-600 mt-3 leading-relaxed">
              Vi guider jer hele vejen, så I aldrig er i tvivl om hvad I skal sige, hvordan kameraet skal vinkles, eller hvordan I sender materialet.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((s, idx) => (
              <div
                key={idx}
                className="bg-white p-7 rounded-2xl border border-slate-200 shadow-xs flex flex-col justify-between"
              >
                <div>
                  <span className="text-xs font-bold text-sky-600 uppercase tracking-wider block mb-2">
                    Trin {s.number}
                  </span>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">
                    {s.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                    {s.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CUSTOMIZED CLINICAL PARTNERSHIP */}
      <section className="py-16 sm:py-24 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-slate-900 to-slate-950 text-white rounded-3xl p-8 sm:p-12 border border-slate-800 shadow-xl">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              
              <div className="lg:col-span-7 space-y-4">
                <span className="text-xs font-bold text-sky-400 uppercase tracking-wider block">
                  Individuel tilpasning
                </span>
                <h3 className="text-2xl sm:text-3xl font-extrabold text-white">
                  Rådgivning formet efter jeres speciale
                </h3>
                <p className="text-sm sm:text-base text-slate-300 leading-relaxed">
                  Hver sundhedspraksis har sit eget faglige univers og sine egne patientbehov. Hvad enten I ønsker grundig sparring om patient-FAQ, vejledning til klinikfotos eller løbende videofeedback, tilpasser vi forløbet præcist til jer.
                </p>
                <p className="text-sm sm:text-base text-slate-300 leading-relaxed">
                  Vi lægger vægt på en tæt og ukompliceret dialog, hvor I altid kan trække på vores faglige sparring, når det passer ind i jeres kalender.
                </p>
              </div>

              <div className="lg:col-span-5 bg-slate-800/80 rounded-2xl p-6 sm:p-8 border border-slate-700/80 text-left space-y-4">
                <div className="w-10 h-10 rounded-xl bg-sky-500/20 text-sky-400 flex items-center justify-center font-bold text-lg">
                  <i className="fa-solid fa-lightbulb" aria-hidden="true" />
                </div>
                <h4 className="text-xl font-bold text-white leading-snug">
                  Få sparring på jeres indhold
                </h4>
                <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
                  Lad os tage en uforpligtende snak om, hvordan I med enkle greb kan skabe fængende og tryghedsskabende sundhedsformidling.
                </p>
                <div className="pt-2">
                  <Link
                    to="/kontakt"
                    className="w-full inline-flex items-center justify-center gap-2 py-3.5 px-6 rounded-full bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold text-sm transition shadow-md"
                  >
                    <span>Book en samtale</span>
                    <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                  </Link>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section className="py-16 sm:py-24 bg-slate-900 text-white text-center">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white">
            Klar til at komme i gang med vejledning?
          </h2>
          <p className="text-base sm:text-lg text-slate-300 max-w-2xl mx-auto">
            Book en uforpligtende samtale, hvor vi drøfter jeres klinik og viser, hvor nemt I selv kan skyde godt materiale med mobilen.
          </p>
          <div className="pt-2">
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold transition shadow-xl"
            >
              <span>Book en samtale</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
}
